import React, { useMemo } from 'react';
import { Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { resolveLogoUrl } from 'PDF/TemplateEngine/utils/resolveLogoUrl';
import { useTemplateColors } from 'PDF/TemplateEngine/TemplateColorsContext';
import { replaceVariables } from 'PDF/TemplateEngine/utils/replaceVariables';
import PortraitBackground, { isDarkPattern } from 'PDF/TemplateEngine/atoms/PortraitBackground';
import { tint } from 'PDF/TemplateEngine/utils/mixColor';

// Portada full-page editable desde el editor low-code. Se monta como una
// <Page> ANTES del body del template (el TemplateRenderer la extrae de
// sections[] y la posiciona como primera página).
//
// Config soportado:
//   - backgroundImage: URL absoluta o path relativo (fondo full-page).
//   - backgroundPattern: fondo vectorial ('bars' | 'none'). Se dibuja ENCIMA de
//     backgroundImage: la imagen queda como textura bajo el velo, igual que en
//     el diseño original. 'bars' es un fondo OSCURO, así que el texto se
//     invierte a tonos claros o no se lee.
//   - overlayColor: color CSS del overlay (rgba permitido). Solo se aplica
//     cuando hay imagen sin patrón — el patrón trae su propio velo.
//   - logo: URL/path override (fallback a config.portraitLogo o config.logo).
//   - subtitle: línea sobre el título, estilo pequeño primary.
//   - title: título principal grande.
//   - folioLabel / folioValue: línea de folio debajo del título.
//   - note: línea de texto libre bajo el folio (el presupuesto la usa para el
//     "Motivo / título corto" del trabajo, vía {reason}).
//   - clientLabel / clientValue: línea de cliente al final.
// Todos los strings soportan interpolación {var} contra `data`.
const resolveUrl = resolveLogoUrl;

function PortraitSection({ section, data, config }) {
  const { colors, FONT } = useTemplateColors();
  const cfg = section?.config || {};
  const dark = isDarkPattern(cfg.backgroundPattern);

  // Sobre un fondo oscuro el texto se invierte a tonos claros. El acento se
  // deriva del primario del tenant (no se hardcodea un azul claro), así el
  // tenant que tenga otra identidad conserva la suya.
  const ink = useMemo(
    () =>
      dark
        ? {
            accent: tint(colors.primary, 0.65),
            strong: '#ffffff',
            body: 'rgba(255,255,255,0.85)',
            soft: 'rgba(255,255,255,0.72)',
          }
        : {
            accent: colors.primary,
            strong: colors.darkText,
            body: colors.darkText,
            soft: colors.muted,
          },
    [dark, colors]
  );

  const styles = useMemo(
    () =>
      StyleSheet.create({
        page: {
          padding: 0,
        },
        bgImage: {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        },
        overlay: {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        },
        content: {
          flex: 1,
          justifyContent: 'center',
          padding: '2.5cm',
        },
        logo: {
          width: '4cm',
          height: '2cm',
          objectFit: 'contain',
          marginBottom: '1cm',
        },
        subtitle: {
          fontSize: 14,
          fontFamily: FONT.bold,
          color: ink.accent,
          marginBottom: '0.3cm',
        },
        title: {
          fontSize: 28,
          fontFamily: FONT.bold,
          color: ink.strong,
        },
        folio: {
          fontSize: 16,
          fontFamily: FONT.base,
          color: ink.body,
          marginTop: '0.6cm',
        },
        note: {
          fontSize: 14,
          fontFamily: FONT.base,
          color: ink.body,
          marginTop: '0.5cm',
          lineHeight: 1.4,
        },
        client: {
          fontSize: 13,
          fontFamily: FONT.base,
          color: ink.soft,
          marginTop: '1.5cm',
        },
      }),
    [ink, FONT]
  );

  const d = data || {};
  const subtitle = replaceVariables(cfg.subtitle || '', d);
  const title = replaceVariables(cfg.title || '', d);
  const folioLabel = replaceVariables(cfg.folioLabel || '', d);
  const folioValue = replaceVariables(cfg.folioValue || '', d);
  const note = replaceVariables(cfg.note || '', d);
  const clientLabel = replaceVariables(cfg.clientLabel || '', d);
  const clientValue = replaceVariables(cfg.clientValue || '', d);
  const bgSrc = resolveUrl(cfg.backgroundImage);
  const logoSrc = resolveUrl(cfg.logo || config?.portraitLogo || config?.logo);
  const overlayColor = cfg.overlayColor || 'rgba(255,255,255,0.3)';

  const folioLine = [folioLabel, folioValue].filter(Boolean).join(' ');
  const clientLine = [clientLabel, clientValue].filter(Boolean).join(' ');

  return (
    <Page size="A4" style={styles.page}>
      {bgSrc ? <Image src={bgSrc} style={styles.bgImage} /> : null}
      {/* El overlay existe para atenuar la imagen de fondo. Con patrón sobra —
          el patrón trae su propio velo — y encima lavaría el fondo oscuro. */}
      {bgSrc && !dark ? <View style={[styles.overlay, { backgroundColor: overlayColor }]} /> : null}
      {/* El patrón se dibuja ENCIMA de la imagen: la foto queda como textura
          bajo el velo, igual que en el diseño original. */}
      <PortraitBackground pattern={cfg.backgroundPattern} />
      <View style={styles.content}>
        {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : null}
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        {title ? <Text style={styles.title}>{title}</Text> : null}
        {folioLine ? <Text style={styles.folio}>{folioLine}</Text> : null}
        {note ? <Text style={styles.note}>{note}</Text> : null}
        {clientLine ? <Text style={styles.client}>{clientLine}</Text> : null}
      </View>
    </Page>
  );
}

export default PortraitSection;
